import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { ArrowRight, Percent, Check, Star, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SEOHead, OfferSchema, BreadcrumbSchema } from '../components/SEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback offers if API is empty or fails
const fallbackOffers = [
  {
    id: 'honeymoon',
    title: 'Honeymoon Escape',
    short_description: 'Celebrate Love in Paradise',
    description: 'Begin your journey together amidst the serene hills of Semiliguda. Our Honeymoon Escape package includes champagne on arrival, couple spa sessions, candlelit dinner under the stars, and a private sunset excursion.',
    original_price: 45000,
    price: 36000,
    valid_until: 'Valid till March 31, 2026',
    image: 'https://images.pexels.com/photos/33914530/pexels-photo-33914530.jpeg',
    is_active: true,
    inclusions: [
      'Executive Suite accommodation',
      'Daily breakfast & dinner',
      'Champagne & chocolate on arrival',
      'Couple spa treatment (90 mins)',
      'Candlelit dinner by the pool',
      'Sunset excursion to Deomali',
      'Late checkout',
    ],
  },
  {
    id: 'corporate',
    title: 'Corporate Retreat',
    short_description: 'Where Business Meets Nature',
    description: 'Elevate your team-building experience with our Corporate Retreat package. Featuring state-of-the-art conference facilities, adventure activities, and gourmet dining, we ensure productive meetings and memorable experiences.',
    original_price: 12000,
    price: 10200,
    valid_until: 'For groups of 10+',
    image: 'https://images.unsplash.com/photo-1745549670488-6852ef218009',
    is_active: true,
    inclusions: [
      'Deluxe Room accommodation',
      'Full board meals',
      'Conference room with AV equipment',
      'Team building activities',
      'Coffee breaks & snacks',
      'Evening bonfire session',
      'Group transport from Koraput',
    ],
  },
  {
    id: 'weekend',
    title: 'Weekend Getaway',
    short_description: 'Your Escape from the Everyday',
    description: 'Unwind with our perfect weekend package. Arrive Friday, leave Sunday feeling completely rejuvenated. Includes nature walks, yoga sessions, and farm-to-table dining experiences.',
    original_price: 25000,
    price: 20000,
    valid_until: 'Friday to Sunday stays',
    image: 'https://images.pexels.com/photos/29347337/pexels-photo-29347337.jpeg',
    is_active: true,
    inclusions: [
      'Deluxe Room with mountain view',
      'Breakfast & dinner daily',
      'Guided morning nature walk',
      'Sunrise yoga session',
      'Kolab Reservoir excursion',
      '15% spa discount',
      'Welcome drink on arrival',
    ],
  },
  {
    id: 'long-stay',
    title: 'Extended Stay',
    short_description: 'Make Emerald Your Home',
    description: 'For stays of 7 nights or more, enjoy our special long-stay rates with additional perks including complimentary room upgrades, daily activities, and personalized concierge service.',
    original_price: 70000,
    price: 52500,
    valid_until: 'Minimum 7 nights',
    image: 'https://images.pexels.com/photos/28247932/pexels-photo-28247932.jpeg',
    is_active: true,
    inclusions: [
      'Complimentary room upgrade',
      'Daily breakfast included',
      '2 dinners complimentary',
      'Airport/station pickup',
      'One spa treatment (60 mins)',
      'Daily housekeeping',
      'Dedicated concierge',
    ],
  },
];

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/offers`);
        // Use API data if available, otherwise use fallback
        if (response.data && response.data.length > 0) {
          setOffers(response.data);
        } else {
          setOffers(fallbackOffers);
        }
      } catch (err) {
        console.warn('Failed to fetch offers, using fallback:', err);
        setOffers(fallbackOffers);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  // Calculate discount percentage
  const getDiscountText = (original, current) => {
    if (!original || original <= current) return null;
    const percent = Math.round(((original - current) / original) * 100);
    return `${percent}% OFF`;
  };

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Offers', url: 'https://thehotelemerald.in/offers' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="offers-page">
      {/* SEO Components */}
      <SEOHead 
        title="Special Offers & Packages | The Hotel Emerald"
        description="Discover exclusive hotel packages and special offers at The Hotel Emerald. Honeymoon escapes, corporate retreats, weekend getaways, and family packages."
        keywords="hotel offers semiliguda, koraput hotel packages, honeymoon package odisha, corporate retreat eastern ghats"
        url="https://thehotelemerald.in/offers"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <OfferSchema offers={offers} />
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/33914530/pexels-photo-33914530.jpeg"
            alt="Special Offers"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/60" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overline text-gold mb-3"
          >
            Special Packages
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl"
          >
            Exclusive Offers
          </motion.h1>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="section-padding">
          <div className="container-custom flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        </section>
      )}

      {/* Offers Grid */}
      {!loading && (
        <section className="section-padding">
          <div className="container-custom">
            <div className="space-y-16">
              {offers.filter(o => o.is_active !== false).map((offer, index) => {
                const discount = getDiscountText(offer.original_price, offer.price);
                
                return (
                  <motion.div
                    key={offer.id}
                    id={offer.id}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={`grid lg:grid-cols-2 gap-8 items-center`}
                    data-testid={`offer-detail-${offer.id}`}
                  >
                    {/* Image */}
                    <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <div className="aspect-[4/3] rounded-sm overflow-hidden">
                        <img
                          src={offer.image}
                          alt={offer.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      
                      {/* Badges */}
                      <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                        {discount && (
                          <Badge className="bg-red-600 text-white">
                            <Percent className="w-3 h-3 mr-1" />
                            {discount}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} lg:pl-8`}>
                      <div className="mb-4">
                        <h2 className="font-serif text-2xl md:text-3xl text-foreground">{offer.title}</h2>
                        {offer.short_description && (
                          <p className="text-muted-foreground text-sm mt-1">{offer.short_description}</p>
                        )}
                      </div>

                      <p className="text-muted-foreground mb-6 leading-relaxed">{offer.description}</p>

                      {/* Pricing */}
                      <div className="flex items-baseline gap-4 mb-6">
                        <span className="text-3xl font-serif text-foreground">
                          ₹{(offer.price || 0).toLocaleString()}
                        </span>
                        {offer.original_price && offer.original_price > offer.price && (
                          <span className="text-lg text-muted-foreground line-through">
                            ₹{offer.original_price.toLocaleString()}
                          </span>
                        )}
                      </div>

                      {/* Inclusions */}
                      {offer.inclusions && offer.inclusions.length > 0 && (
                        <div className="bg-muted/50 rounded-sm p-4 mb-6">
                          <p className="text-sm font-medium text-foreground mb-3">Package Includes:</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {offer.inclusions.map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Validity & CTA */}
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        {offer.valid_until && (
                          <span className="text-sm text-muted-foreground">{offer.valid_until}</span>
                        )}
                        <Button 
                          className="btn-emerald"
                          data-testid={`book-offer-${offer.id}`}
                          onClick={() => window.location.href = `/book?offer=${offer.id}`}
                        >
                          Book This Package
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Terms Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-serif text-2xl text-foreground mb-4">Terms & Conditions</h3>
            <ul className="text-sm text-muted-foreground space-y-2 text-left">
              <li>• All offers are subject to availability and may be withdrawn without prior notice.</li>
              <li>• Advance booking of minimum 7 days required for all packages.</li>
              <li>• Offers cannot be combined with other promotions or discounts.</li>
              <li>• Cancellation policy applies as per our standard terms.</li>
              <li>• Prices are inclusive of all applicable taxes.</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
